import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { EmailContactData } from '../models/EmailContact';
import { EmailDraft, EmailMessage, EmailThread, EmailTransfer } from '../models/Email';
import { EditorState } from 'lexical';

interface EmailContextType {
  templateHtml: string;
  setTemplateHtml: React.Dispatch<React.SetStateAction<string>>;
  selectedEmail: string | null;
  setSelectedEmail: React.Dispatch<React.SetStateAction<string | null>>;
  emailContacts: string[];
  emailData: Record<string, EmailContactData>;
  emailThreads: Record<string, EmailThread>;
  addEmailContact: (contactId: string) => void;
  removeEmailContact: (contactId: string) => void;
  updateEmailData: (contactId: string, data: EmailContactData) => void;
  updateEmailThread: (contactId: string, thread: EmailThread) => void;
  appendMessageToThread: (contactId: string, message: EmailMessage) => void;
  setThreadTransferDetails: (contactId: string, transferDetails: EmailTransfer) => void;
  isReplyOpen: boolean;
  setIsReplyOpen: React.Dispatch<React.SetStateAction<boolean>>;
  closingContactId: string | null;
  setClosingContactId: React.Dispatch<React.SetStateAction<string | null>>;
  outboundContactId: string | null;
  setOutboundContactId: React.Dispatch<React.SetStateAction<string | null>>;
  emailDrafts: Record<string, EmailDraft>;
  getEmailDraftBodyState: (contactId: string) => EditorState | undefined;
  updateEmailDraft: (contactId: string, draft: Partial<EmailDraft>) => void;
  clearEmailDraft: (contactId: string) => void;
  isLoadingThread: (contactId: string) => boolean;
  startLoadingThread: (contactId: string) => void;
  stopLoadingThread: (contactId: string) => void;
}

const EmailContext = createContext<EmailContextType>({
  templateHtml: '',
  setTemplateHtml: () => {},
  selectedEmail: null,
  setSelectedEmail: () => {},
  emailContacts: [],
  emailData: {},
  emailThreads: {},
  addEmailContact: () => {},
  removeEmailContact: () => {},
  updateEmailData: () => {},
  updateEmailThread: () => {},
  appendMessageToThread: () => {},
  setThreadTransferDetails: () => {},
  isReplyOpen: false,
  setIsReplyOpen: () => {},
  closingContactId: null,
  setClosingContactId: () => {},
  outboundContactId: null,
  setOutboundContactId: () => {},
  emailDrafts: {},
  getEmailDraftBodyState: () => undefined,
  updateEmailDraft: () => {},
  clearEmailDraft: () => {},
  isLoadingThread: () => false,
  startLoadingThread: () => {},
  stopLoadingThread: () => {}
});

export const useEmailContext = (): EmailContextType => useContext(EmailContext);

interface EmailProviderProps {
  children: React.ReactNode;
}

export const EmailProvider: React.FC<EmailProviderProps> = ({ children }) => {
  const [selectedEmail, setSelectedEmail] = useState<string | null>(null);
  const [emailContacts, setEmailContacts] = useState<string[]>([]);
  const [emailData, setEmailData] = useState<Record<string, EmailContactData>>({});
  const [emailThreads, setEmailThreads] = useState<Record<string, EmailThread>>({});
  const [isReplyOpen, setIsReplyOpen] = useState(false);
  const [closingContactId, setClosingContactId] = useState<string | null>(null);
  const [outboundContactId, setOutboundContactId] = useState<string | null>(null);
  const [templateHtml, setTemplateHtmlState] = useState('');
  const [emailDrafts, setEmailDrafts] = useState<Record<string, EmailDraft>>({});
  const draftBodyStateRef = useRef<Record<string, EditorState>>({});
  const [loadingThreads, setLoadingThreads] = useState<Set<string>>(new Set());
  const isLoadingThread = useCallback((contactId: string) => loadingThreads.has(contactId), [loadingThreads]);

  useEffect(() => {
    if (!selectedEmail) {
      setTemplateHtmlState('');
      return;
    }

    setTemplateHtmlState(emailDrafts[selectedEmail]?.templateHtml ?? '');
  }, [emailDrafts, selectedEmail]);

  useEffect(() => {
    if (!selectedEmail) {
      return;
    }

    const selectedBodyState = draftBodyStateRef.current[selectedEmail];

    if (!selectedBodyState) {
      return;
    }

    setEmailDrafts(prev => {
      if (prev[selectedEmail]?.bodyState === selectedBodyState) {
        return prev;
      }

      return {
        ...prev,
        [selectedEmail]: {
          ...prev[selectedEmail],
          bodyState: selectedBodyState
        }
      };
    });
  }, [selectedEmail]);

  const startLoadingThread = useCallback((contactId: string) => {
    setLoadingThreads(prev => {
      if (prev.has(contactId)) return prev;
      const next = new Set(prev);
      next.add(contactId);
      return next;
    });
  }, []);

  const stopLoadingThread = useCallback((contactId: string) => {
    setLoadingThreads(prev => {
      if (!prev.has(contactId)) return prev;
      const next = new Set(prev);
      next.delete(contactId);
      return next;
    });
  }, []);

  const addEmailContact = useCallback((contactId: string) => {
    setEmailContacts(prev => (prev.includes(contactId) ? prev : [...prev, contactId]));
  }, []);

  const removeEmailContact = useCallback((contactId: string) => {
    setEmailContacts(prev => prev.filter(id => id !== contactId));

    setEmailData(prev => {
      const next = { ...prev };
      delete next[contactId];
      return next;
    });

    setEmailThreads(prev => {
      const next = { ...prev };
      delete next[contactId];
      return next;
    });

    setEmailDrafts(prev => {
      const next = { ...prev };
      delete next[contactId];
      return next;
    });

    delete draftBodyStateRef.current[contactId];

    setOutboundContactId(prev => (prev === contactId ? null : prev));
  }, []);

  const updateEmailData = useCallback((contactId: string, data: EmailContactData) => {
    setEmailData(prev => ({ ...prev, [contactId]: data }));
  }, []);

  const updateEmailDraft = useCallback((contactId: string, draft: Partial<EmailDraft>) => {
    if (draft.bodyState) {
      draftBodyStateRef.current[contactId] = draft.bodyState;
    }

    const stateDraft = { ...draft };
    delete stateDraft.bodyState;

    if (Object.keys(stateDraft).length === 0) {
      return;
    }

    setEmailDrafts(prev => ({
      ...prev,
      [contactId]: {
        ...prev[contactId],
        ...stateDraft
      }
    }));
  }, []);

  const getEmailDraftBodyState = useCallback((contactId: string) => {
    return draftBodyStateRef.current[contactId];
  }, []);

  const clearEmailDraft = useCallback((contactId: string) => {
    delete draftBodyStateRef.current[contactId];

    setEmailDrafts(prev => {
      const next = { ...prev };
      delete next[contactId];
      return next;
    });
  }, []);

  const setTemplateHtml = useCallback<React.Dispatch<React.SetStateAction<string>>>(
    value => {
      setTemplateHtmlState(prevValue => {
        const nextTemplateHtml = typeof value === 'function' ? value(prevValue) : value;

        if (selectedEmail) {
          setEmailDrafts(prevDrafts => ({
            ...prevDrafts,
            [selectedEmail]: {
              ...prevDrafts[selectedEmail],
              templateHtml: nextTemplateHtml
            }
          }));
        }

        return nextTemplateHtml;
      });
    },
    [selectedEmail]
  );

  const updateEmailThread = useCallback((contactId: string, thread: EmailThread) => {
    setEmailThreads(prev => ({ ...prev, [contactId]: thread }));
  }, []);

  const appendMessageToThread = useCallback((contactId: string, message: EmailMessage) => {
    setEmailThreads(prev => {
      const existing = prev[contactId];
      if (!existing) return prev;
      return { ...prev, [contactId]: { ...existing, messages: [...existing.messages, message] } };
    });
  }, []);

  const setThreadTransferDetails = useCallback((contactId: string, transferDetails: EmailTransfer) => {
    setEmailThreads(prev => {
      const existing = prev[contactId];
      if (!existing) return prev;
      return { ...prev, [contactId]: { ...existing, transferDetails } };
    });
  }, []);

  return (
    <EmailContext.Provider
      value={{
        templateHtml,
        setTemplateHtml,
        selectedEmail,
        setSelectedEmail,
        emailContacts,
        emailData,
        emailThreads,
        addEmailContact,
        removeEmailContact,
        updateEmailData,
        updateEmailThread,
        appendMessageToThread,
        setThreadTransferDetails,
        isReplyOpen,
        setIsReplyOpen,
        closingContactId,
        setClosingContactId,
        outboundContactId,
        setOutboundContactId,
        emailDrafts,
        getEmailDraftBodyState,
        updateEmailDraft,
        clearEmailDraft,
        isLoadingThread,
        startLoadingThread,
        stopLoadingThread
      }}
    >
      {children}
    </EmailContext.Provider>
  );
};
